"use client";

import { useMemo, useState, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAuth } from "@/hook/useAuth";
import { LeadPlatform } from "@/store/feature/enquiry/leadApi";
import AnonymousRouteLeadModal from "./AnonymousRouteLeadModal";

type RouteModalConfig = {
	key: string;
	path: string;
	source: string;
	platform: LeadPlatform;
	requiresPerfectMatchesQuery?: boolean;
};

type RouteModalBackoffMeta = {
	shownCount: number;
	completed: boolean;
	awaitingSecondShow: boolean;
};

const ROUTE_MODAL_CONFIGS: RouteModalConfig[] = [
	{
		key: "global_search",
		path: "/ai-course-finder",
		source: "global_search_follow_up_modal",
		platform: "global_search",
	},
	{
		key: "roi_calculator",
		path: "/roi-calculator",
		source: "roi_calculator_follow_up_modal",
		platform: "website",
	},
	{
		key: "find_scholarships",
		path: "/find-scholarships",
		source: "find_scholarships_follow_up_modal",
		platform: "website",
	},
	{
		key: "explore_courses",
		path: "/explore-courses",
		source: "explore_courses_follow_up_modal",
		platform: "manual_course_search",
	},
	{
		key: "course_finder_eligibility_results",
		path: "/ai-course-finder/eligibility",
		source: "course_finder_eligibility_results_follow_up_modal",
		platform: "course_finder",
		requiresPerfectMatchesQuery: true,
	},
	{
		key: "course_finder_program_results",
		path: "/ai-course-finder/program",
		source: "course_finder_program_results_follow_up_modal",
		platform: "course_finder",
		requiresPerfectMatchesQuery: true,
	},
	{
		key: "course_finder_institutes_results",
		path: "/ai-course-finder/institutes",
		source: "course_finder_institutes_results_follow_up_modal",
		platform: "course_finder",
		requiresPerfectMatchesQuery: true,
	},
];

const FIRST_OPEN_DELAY_MS = 15 * 1000;
const SECOND_OPEN_INACTIVITY_DELAY_MS = 30 * 1000;
const STORAGE_PREFIX = "anonymous_follow_up_backoff_v3_schedule";

const normalizePathname = (pathname: string) => {
	const cleanedPath = pathname.replace(/\/+$/, "");
	return cleanedPath || "/";
};

const getDefaultBackoffMeta = (): RouteModalBackoffMeta => ({
	shownCount: 0,
	completed: false,
	awaitingSecondShow: false,
});

const readBackoffMeta = (storageKey: string): RouteModalBackoffMeta => {
	try {
		const rawValue = sessionStorage.getItem(storageKey);
		if (!rawValue) {
			return getDefaultBackoffMeta();
		}

		const parsedValue = JSON.parse(rawValue) as Partial<RouteModalBackoffMeta>;
		const shownCount = Number(parsedValue.shownCount);
		const completed = Boolean(parsedValue.completed);
		const awaitingSecondShow = Boolean(parsedValue.awaitingSecondShow);

		if (!Number.isFinite(shownCount)) {
			return getDefaultBackoffMeta();
		}

		return { shownCount, completed, awaitingSecondShow };
	} catch {
		return getDefaultBackoffMeta();
	}
};

const writeBackoffMeta = (
	storageKey: string,
	meta: RouteModalBackoffMeta,
) => {
	sessionStorage.setItem(storageKey, JSON.stringify(meta));
};

const AnonymousRouteFollowUpModal = () => {
	const { isAnonymous, loading } = useAuth();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const [isOpen, setIsOpen] = useState(false);

	const query = searchParams.get("query")?.toLowerCase().trim() ?? "";

	const activeRouteConfig = useMemo(() => {
		const currentPath = normalizePathname(pathname);

		return (
			ROUTE_MODAL_CONFIGS.find((config) => {
				if (config.path !== currentPath) {
					return false;
				}

				if (config.requiresPerfectMatchesQuery) {
					return query === "perfect-matches";
				}

				return true;
			}) ?? null
		);
	}, [pathname, query]);

	const storageKey = activeRouteConfig
		? `${STORAGE_PREFIX}:${activeRouteConfig.key}`
		: null;

	useEffect(() => {
		if (loading || !isAnonymous || !activeRouteConfig || !storageKey || isOpen) {
			return;
		}

		const { shownCount, completed } = readBackoffMeta(storageKey);
		if (completed || shownCount >= 1) {
			return;
		}

		const timer = window.setTimeout(() => {
			const currentMeta = readBackoffMeta(storageKey);
			if (currentMeta.completed || currentMeta.shownCount >= 1) {
				return;
			}

			setIsOpen(true);
			writeBackoffMeta(storageKey, {
				shownCount: 1,
				completed: false,
				awaitingSecondShow: false,
			});
		}, FIRST_OPEN_DELAY_MS);

		return () => window.clearTimeout(timer);
	}, [loading, isAnonymous, activeRouteConfig, storageKey, isOpen]);

	useEffect(() => {
		if (loading || !isAnonymous || !activeRouteConfig || !storageKey || isOpen) {
			return;
		}

		const currentMeta = readBackoffMeta(storageKey);
		if (
			currentMeta.completed ||
			currentMeta.shownCount !== 1 ||
			!currentMeta.awaitingSecondShow
		) {
			return;
		}

		let inactivityTimer: number | null = null;

		const scheduleSecondPopup = () => {
			if (inactivityTimer) {
				window.clearTimeout(inactivityTimer);
			}

			inactivityTimer = window.setTimeout(() => {
				const latestMeta = readBackoffMeta(storageKey);
				if (
					latestMeta.completed ||
					latestMeta.shownCount !== 1 ||
					!latestMeta.awaitingSecondShow
				) {
					return;
				}

				setIsOpen(true);
				writeBackoffMeta(storageKey, {
					shownCount: 2,
					completed: true,
					awaitingSecondShow: false,
				});
			}, SECOND_OPEN_INACTIVITY_DELAY_MS);
		};

		const handleActivity = () => {
			scheduleSecondPopup();
		};

		scheduleSecondPopup();

		window.addEventListener("mousemove", handleActivity);
		window.addEventListener("mousedown", handleActivity);
		window.addEventListener("keydown", handleActivity);
		window.addEventListener("scroll", handleActivity, { passive: true });
		window.addEventListener("touchstart", handleActivity, { passive: true });

		return () => {
			if (inactivityTimer) {
				window.clearTimeout(inactivityTimer);
			}

			window.removeEventListener("mousemove", handleActivity);
			window.removeEventListener("mousedown", handleActivity);
			window.removeEventListener("keydown", handleActivity);
			window.removeEventListener("scroll", handleActivity);
			window.removeEventListener("touchstart", handleActivity);
		};
	}, [loading, isAnonymous, activeRouteConfig, storageKey, isOpen]);

	const handleDismissWithoutSubmit = () => {
		if (storageKey) {
			const currentMeta = readBackoffMeta(storageKey);
			writeBackoffMeta(storageKey, {
				shownCount: currentMeta.shownCount,
				completed: currentMeta.completed,
				awaitingSecondShow:
					currentMeta.shownCount === 1 && !currentMeta.completed,
			});
		}

		setIsOpen(false);
	};

	const handleSubmitSuccess = () => {
		if (storageKey) {
			writeBackoffMeta(storageKey, {
				shownCount: 2,
				completed: true,
				awaitingSecondShow: false,
			});
		}

		setIsOpen(false);
	};

	if (!activeRouteConfig) {
		return null;
	}

	return (
		<AnonymousRouteLeadModal
			isOpen={isOpen}
			setIsOpen={setIsOpen}
			isAnonymous={isAnonymous}
			platform={activeRouteConfig.platform}
			source={activeRouteConfig.source}
			contactButtonText="Join Now"
			onClose={handleDismissWithoutSubmit}
			onContactSuccess={handleSubmitSuccess}
			data={{
				triggerType: "route_auto_popup",
				routeKey: activeRouteConfig.key,
			}}
		/>
	);
};

export default AnonymousRouteFollowUpModal;
